/**
 * Pure helpers for the Chat Output View — kept free of React / auth / API
 * imports so unit tests can load them without Electron's `app`.
 */

import {
  isAskUserQuestionStagedAction,
  parseAskUserQuestionInput,
  type AskUserQuestionAnswerItem,
} from '@renderer/chat/integrations/ask-user-question/types'
import {
  feedbackQuestionText,
  isAskUserFeedbackStagedAction,
  parseAskUserFeedbackInput,
  type AskUserFeedbackResponse,
} from '@renderer/chat/integrations/ask-user-feedback/types'
import type { AnswerableProgress } from '@renderer/chat/integrations/answerable'

/**
 * One question/answer pair the output view renders as its own settled row.
 * Structural rather than the tools' own types, so a background AskUserQuestion
 * answer and an AskUserFeedback completion render through the same path.
 */
export interface ResolvedQuestionOutput {
  question: { question: string; header: string }
  answer: { selected: Array<string>; explanation?: string }
}

interface AnswerableActionRow {
  toolSet?: string
  toolName?: string
  inputData?: unknown
  outputData?: unknown
}

export function questionAssistantResponse(action: { outputData?: unknown }): string | undefined {
  const output = action.outputData
  if (!output || typeof output !== 'object' || Array.isArray(output)) {
    return undefined
  }
  const response = (output as { assistantResponse?: unknown }).assistantResponse
  if (typeof response !== 'string') {
    return undefined
  }
  return response.trim() || undefined
}

/**
 * The closing text the given chat wrote after touching this action. Other chats
 * (say, one that edited the draft) leave their own captions; only the owning
 * conversation's caption belongs under the card in its output view.
 */
export function actionChatCaption(action: CaptionedAction, conversationId: string): string | undefined {
  return findChatCaption(action, conversationId)?.text.trim() || undefined
}

interface CaptionedAction {
  chatCaptions?: Array<{ conversationId: string; text: string; llmMessageId?: string; userInitiated?: boolean }>
}

function findChatCaption(action: CaptionedAction, conversationId: string) {
  return action.chatCaptions?.find(
    (candidate) => candidate.conversationId === conversationId && candidate.userInitiated === true,
  )
}

/** Keeps a turn's caption on only the last action it touched. */
export function latestActionChatCaptions(
  actions: ReadonlyArray<CaptionedAction & { id: string }>,
  conversationId: string,
): Map<string, string> {
  const latestActionByCaption = new Map<string, { actionId: string; text: string }>()
  for (const action of actions) {
    const caption = findChatCaption(action, conversationId)
    const text = caption?.text.trim()
    if (!caption || !text) {
      continue
    }
    // Legacy captions lack a message id, so their text is the only stable deduplication key.
    latestActionByCaption.set(caption.llmMessageId ?? text, { actionId: action.id, text })
  }
  return new Map([...latestActionByCaption.values()].map(({ actionId, text }) => [actionId, text]))
}

export function shouldAnimateActionControlsHeight({
  previousActionId,
  nextActionId,
  lastActionId,
  lastActionHasCaption,
}: {
  previousActionId: string | undefined
  nextActionId: string | undefined
  lastActionId: string | undefined
  lastActionHasCaption: boolean
}): boolean {
  return [previousActionId, nextActionId].some(
    (actionId) => actionId !== undefined && (actionId !== lastActionId || lastActionHasCaption),
  )
}

export function answeredQuestionOutputs(
  action: { inputData?: unknown },
  output: { answers?: Array<AskUserQuestionAnswerItem> } | undefined,
): Array<ResolvedQuestionOutput> {
  const input = parseAskUserQuestionInput(action.inputData)
  if (!input || !Array.isArray(output?.answers)) {
    return []
  }

  return input.questions.flatMap((question) => {
    const answer = output.answers?.find((candidate) => candidate?.header === question.header)
    return answer && answer.selected.length > 0 ? [{ question, answer }] : []
  })
}

/** The feedback half of `answeredQuestionOutputs`: locally-filled blanks, in blank order. */
export function answeredFeedbackOutputs(
  action: { inputData?: unknown },
  output: { responses?: Array<AskUserFeedbackResponse> } | undefined,
): Array<ResolvedQuestionOutput> {
  const input = parseAskUserFeedbackInput(action.inputData)
  if (!input || !Array.isArray(output?.responses)) {
    return []
  }

  return input.blanks.flatMap((blank) => {
    const response = output.responses?.find((candidate) => candidate?.header === blank.header)
    return response && response.filled.trim().length > 0
      ? [
          {
            question: { question: feedbackQuestionText(blank), header: blank.header },
            answer: { selected: [response.filled.trim()] },
          },
        ]
      : []
  })
}

/** Dispatches to whichever answerable tool the action is; empty for neither. */
export function answeredAnswerableOutputs(
  action: AnswerableActionRow,
  output: AnswerableProgress | undefined,
): Array<ResolvedQuestionOutput> {
  if (isAskUserQuestionStagedAction(action)) {
    return answeredQuestionOutputs(action, output as { answers?: Array<AskUserQuestionAnswerItem> } | undefined)
  }
  if (isAskUserFeedbackStagedAction(action)) {
    return answeredFeedbackOutputs(action, output as { responses?: Array<AskUserFeedbackResponse> } | undefined)
  }
  return []
}

export type ChatOutputFloatingRegion = 'questions' | 'single-action-toolbar' | 'batch-toolbar' | 'working' | null

export function resolveChatOutputFloatingRegion({
  hasPendingQuestions,
  actionCount,
  selectedActionHasOwnToolbar,
  isStreaming = false,
}: {
  hasPendingQuestions: boolean
  actionCount: number
  selectedActionHasOwnToolbar: boolean
  isStreaming?: boolean
}): ChatOutputFloatingRegion {
  if (hasPendingQuestions) {
    return 'questions'
  }
  const fallback = isStreaming ? 'working' : null
  if (selectedActionHasOwnToolbar) {
    return fallback
  }
  if (actionCount === 1) {
    return 'single-action-toolbar'
  }
  return actionCount > 1 ? 'batch-toolbar' : fallback
}

/**
 * Expands one resolved multi-question action into the question/answer pairs the
 * output view renders as independent tool calls. A malformed or partial result
 * stays on the legacy whole-card path rather than silently dropping a question.
 */
export function resolvedQuestionOutputs(action: {
  inputData?: unknown
  outputData?: unknown
}): Array<ResolvedQuestionOutput> | null {
  const input = parseAskUserQuestionInput(action.inputData)
  const output = action.outputData
  if (!input || !output || typeof output !== 'object' || !Array.isArray((output as { answers?: unknown }).answers)) {
    return null
  }

  const answers = (output as { answers: Array<AskUserQuestionAnswerItem> }).answers
  const pairs = input.questions.map((question): ResolvedQuestionOutput | null => {
    const answer = answers.find((candidate) => candidate?.header === question.header)
    return answer ? { question, answer } : null
  })

  return pairs.every((pair): pair is ResolvedQuestionOutput => pair !== null) ? pairs : null
}

/**
 * The feedback half of `resolvedQuestionOutputs`: one pair per blank, from the
 * posted completions. Same malformed/partial rule — a payload missing a blank
 * stays on the legacy whole-card path rather than silently dropping it. A
 * skipped blank keeps its pair with no answer, mirroring the card's own
 * resolved rendering.
 */
export function resolvedFeedbackOutputs(action: {
  inputData?: unknown
  outputData?: unknown
}): Array<ResolvedQuestionOutput> | null {
  const input = parseAskUserFeedbackInput(action.inputData)
  const output = action.outputData
  if (
    !input ||
    !output ||
    typeof output !== 'object' ||
    !Array.isArray((output as { responses?: unknown }).responses)
  ) {
    return null
  }

  const responses = (output as { responses: Array<AskUserFeedbackResponse> }).responses
  const pairs = input.blanks.map((blank) => {
    const response = responses.find((candidate) => candidate?.header === blank.header)
    return response
      ? {
          question: { question: feedbackQuestionText(blank), header: blank.header },
          answer: { selected: response.filled.trim().length > 0 ? [response.filled.trim()] : [] },
        }
      : null
  })

  return pairs.every((pair): pair is ResolvedQuestionOutput => pair !== null) ? pairs : null
}

/** Dispatches to whichever answerable tool the action is; null for neither. */
export function resolvedAnswerableOutputs(action: AnswerableActionRow): Array<ResolvedQuestionOutput> | null {
  if (isAskUserQuestionStagedAction(action)) {
    return resolvedQuestionOutputs(action)
  }
  if (isAskUserFeedbackStagedAction(action)) {
    return resolvedFeedbackOutputs(action)
  }
  return null
}

/** Formats complete ask-user output for the optimistic resume bubble. */
export function answerableResumeLabel(action: AnswerableActionRow): string | null {
  const pairs = resolvedAnswerableOutputs(action)
  if (!pairs || pairs.length === 0) {
    return null
  }
  return pairs
    .map(({ question, answer }) => {
      const chosen = answer.selected.length > 0 ? answer.selected.join(', ') : 'Skipped'
      const why = answer.explanation?.trim()
      return `Q: ${question.question}\nA: ${why ? `${chosen} (${why})` : chosen}`
    })
    .join('\n\n')
}

/**
 * How many steps an answerable action puts in front of the user — the count
 * the floating bar's combined progress adds up. Null when the action is not an
 * answerable, or its input can't be parsed.
 */
export function answerableStepCount(action: AnswerableActionRow): number | null {
  if (isAskUserQuestionStagedAction(action)) {
    return parseAskUserQuestionInput(action.inputData)?.questions.length ?? null
  }
  if (isAskUserFeedbackStagedAction(action)) {
    return parseAskUserFeedbackInput(action.inputData)?.blanks.length ?? null
  }
  return null
}

/** Newest assistant message with non-empty text content. */
export function lastAssistantText(
  messages: Array<{ role?: string; content?: unknown }> | undefined,
): string | undefined {
  if (!messages?.length) {
    return undefined
  }

  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i]
    if (message?.role !== 'assistant') {
      continue
    }
    if (typeof message.content !== 'string') {
      continue
    }
    const text = message.content.trim()
    if (text) {
      return text
    }
  }

  return undefined
}

export function orderStagedActionsChronologically<T extends { id: string; createdAt: string }>(
  actions: ReadonlyArray<T>,
): Array<T> {
  return [...actions].sort((a, b) => {
    const createdAtDifference = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    return createdAtDifference || a.id.localeCompare(b.id)
  })
}

/**
 * Runs a group approval one action at a time, preserving the order shown in
 * the output. A failed action does not strand the actions behind it: each
 * action owns and surfaces its own invoke error in the card.
 */
export async function executeStagedActionsSequentially(
  actionIds: ReadonlyArray<string>,
  invoke: (actionId: string) => Promise<void>,
  onError?: (error: unknown, actionId: string) => void,
): Promise<void> {
  for (const actionId of actionIds) {
    try {
      await invoke(actionId)
    } catch (error) {
      onError?.(error, actionId)
    }
  }
}
